import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
    Field,
    FieldContent,
    FieldError,
    FieldGroup,
    FieldLabel,
} from '@/components/ui/field'
import { Form, FormField } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/components/ui/card'
import { CheckCircle2, Loader2 } from 'lucide-react'
import { usePostHog } from '@posthog/react'

const waitlistFormSchema = z
    .object({
        email: z.string().email('Enter a valid email address.'),
        firstName: z.string().min(2, 'First name must be at least 2 characters.'),
        featureInterest: z.string().optional(),
        featureOther: z.string().optional(),
        creatureInterest: z.string().optional(),
        creatureOther: z.string().optional(),
        feedback: z.string().optional(),
    })
    .refine(
        (data) => {
            if (data.featureInterest === 'other') {
                return data.featureOther && data.featureOther.trim().length > 0
            }
            return true
        },
        {
            message: 'Please specify the feature.',
            path: ['featureOther'],
        }
    )
    .refine(
        (data) => {
            if (data.creatureInterest === 'other') {
                return data.creatureOther && data.creatureOther.trim().length > 0
            }
            return true
        },
        {
            message: 'Please specify the creature.',
            path: ['creatureOther'],
        }
    )

type WaitlistFormValues = z.infer<typeof waitlistFormSchema>

export const WaitlistFormSection = () => {
    const [submitted, setSubmitted] = useState(false)
    const [submitError, setSubmitError] = useState<string | null>(null)
    const posthog = usePostHog()
    const form = useForm<WaitlistFormValues>({
        resolver: zodResolver(waitlistFormSchema),
        defaultValues: {
            email: '',
            firstName: '',
            featureInterest: '',
            featureOther: '',
            creatureInterest: '',
            creatureOther: '',
            feedback: '',
        },
    })

    const featureInterest = form.watch('featureInterest')
    const creatureInterest = form.watch('creatureInterest')

    const onSubmit = async (values: WaitlistFormValues) => {
        setSubmitError(null)
        try {
            const response = await fetch('https://formspree.io/f/xvgdprep', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
                body: JSON.stringify(values),
            })

            if (!response.ok) {
                throw new Error(`Form submission failed with status ${response.status}`)
            }

            posthog?.capture('waitlist_form_submitted', {
                featureInterest: values.featureInterest || 'unspecified',
                creatureInterest: values.creatureInterest || 'unspecified',
                hasFeedback: Boolean(values.feedback?.trim()),
            })
            setSubmitted(true)
        } catch (error) {
            console.error('Waitlist form submission error', error)
            setSubmitError('Something went wrong while sending your request. Please try again.')
        }
    }

    return (
        <section id="contact" className="mb-16 scroll-mt-16">
            <Card>
                <CardHeader>
                    <CardTitle>Get Early Access</CardTitle>
                    <CardDescription>
                        Get updates only when there’s something worth saying. No spam. Early Access members can
                        help shape features, creatures, quests, and the future of the realms.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="max-w-xl mx-auto">
                        <Card>
                            <CardContent className="pt-6">
                                {submitted ? (
                                    <div className="rounded-lg border border-primary/30 bg-primary/10 p-6 text-sm">
                                        <div className="flex items-start gap-3">
                                            <CheckCircle2 className="mt-0.5 size-5 text-primary" />
                                            <div className="flex-1">
                                                <p className="font-medium">Request sent</p>
                                                <p className="text-muted-foreground mt-1">
                                                    Thanks for joining Early Access! We&apos;ll email you when there&apos;s
                                                    something worth sharing.
                                                </p>
                                                <div className="mt-4 flex justify-end">
                                                    <Button
                                                        onClick={() => {
                                                            form.reset()
                                                            setSubmitted(false)
                                                        }}
                                                    >
                                                        OK
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <Form {...form}>
                                        <form className="space-y-6" onSubmit={form.handleSubmit(onSubmit)}>
                                            {submitError && (
                                                <div className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                                                    {submitError}
                                                </div>
                                            )}
                                            <FieldGroup className="gap-6">
                                                <FormField
                                                    control={form.control}
                                                    name="email"
                                                    render={({ field, fieldState }) => (
                                                        <Field data-invalid={!!fieldState.error}>
                                                            <FieldLabel htmlFor="email">
                                                                Email Address <span className="text-destructive">*</span>
                                                            </FieldLabel>
                                                            <FieldContent>
                                                                <Input
                                                                    {...field}
                                                                    id="email"
                                                                    type="email"
                                                                    placeholder="Enter your email address"
                                                                    disabled={form.formState.isSubmitting}
                                                                />
                                                                <FieldError
                                                                    errors={fieldState.error ? [fieldState.error] : undefined}
                                                                />
                                                            </FieldContent>
                                                        </Field>
                                                    )}
                                                />

                                                <FormField
                                                    control={form.control}
                                                    name="firstName"
                                                    render={({ field, fieldState }) => (
                                                        <Field data-invalid={!!fieldState.error}>
                                                            <FieldLabel htmlFor="firstName">
                                                                First Name <span className="text-destructive">*</span>
                                                            </FieldLabel>
                                                            <FieldContent>
                                                                <Input
                                                                    {...field}
                                                                    id="firstName"
                                                                    placeholder="Enter your first name"
                                                                    disabled={form.formState.isSubmitting}
                                                                />
                                                                <FieldError
                                                                    errors={fieldState.error ? [fieldState.error] : undefined}
                                                                />
                                                            </FieldContent>
                                                        </Field>
                                                    )}
                                                />
                                            </FieldGroup>

                                            <Separator />

                                            <div className="space-y-6">
                                                <h3 className="text-lg font-semibold">Help Shape the Realms (Optional)</h3>

                                                <FormField
                                                    control={form.control}
                                                    name="featureInterest"
                                                    render={({ field, fieldState }) => (
                                                        <Field data-invalid={!!fieldState.error}>
                                                            <FieldLabel htmlFor="featureInterest">
                                                                What should we build first?
                                                            </FieldLabel>
                                                            <FieldContent>
                                                                <Select
                                                                    value={field.value}
                                                                    disabled={form.formState.isSubmitting}
                                                                    onValueChange={(value) => {
                                                                        field.onChange(value)
                                                                        if (value !== 'other') {
                                                                            form.setValue('featureOther', '')
                                                                        }
                                                                    }}
                                                                >
                                                                    <SelectTrigger id="featureInterest" className="w-full">
                                                                        <SelectValue placeholder="Select a feature" />
                                                                    </SelectTrigger>
                                                                    <SelectContent>
                                                                        <SelectItem value="forums">Forums</SelectItem>
                                                                        <SelectItem value="lore">Lore</SelectItem>
                                                                        <SelectItem value="breeding">Breeding</SelectItem>
                                                                        <SelectItem value="questing">Questing</SelectItem>
                                                                        <SelectItem value="adoptions">Adoptions</SelectItem>
                                                                        <SelectItem value="artwork">Artwork</SelectItem>
                                                                        <SelectItem value="not-sure">I'm not sure</SelectItem>
                                                                        <SelectItem value="other">Other</SelectItem>
                                                                    </SelectContent>
                                                                </Select>
                                                                <FieldError
                                                                    errors={fieldState.error ? [fieldState.error] : undefined}
                                                                />
                                                            </FieldContent>
                                                        </Field>
                                                    )}
                                                />

                                                {featureInterest === 'other' && (
                                                    <FormField
                                                        control={form.control}
                                                        name="featureOther"
                                                        render={({ field, fieldState }) => (
                                                            <Field data-invalid={!!fieldState.error}>
                                                                <FieldLabel htmlFor="featureOther">Please specify</FieldLabel>
                                                                <FieldContent>
                                                                    <Input
                                                                        {...field}
                                                                        id="featureOther"
                                                                        placeholder="Enter the feature you're excited about"
                                                                        disabled={form.formState.isSubmitting}
                                                                    />
                                                                    <FieldError
                                                                        errors={fieldState.error ? [fieldState.error] : undefined}
                                                                    />
                                                                </FieldContent>
                                                            </Field>
                                                        )}
                                                    />
                                                )}

                                                <FormField
                                                    control={form.control}
                                                    name="creatureInterest"
                                                    render={({ field, fieldState }) => (
                                                        <Field data-invalid={!!fieldState.error}>
                                                            <FieldLabel htmlFor="creatureInterest">
                                                                Which magical creature should we add next?
                                                            </FieldLabel>
                                                            <FieldContent>
                                                                <Select
                                                                    value={field.value}
                                                                    disabled={form.formState.isSubmitting}
                                                                    onValueChange={(value) => {
                                                                        field.onChange(value)
                                                                        if (value !== 'other') {
                                                                            form.setValue('creatureOther', '')
                                                                        }
                                                                    }}
                                                                >
                                                                    <SelectTrigger id="creatureInterest" className="w-full">
                                                                        <SelectValue placeholder="Select a creature" />
                                                                    </SelectTrigger>
                                                                    <SelectContent>
                                                                        <SelectItem value="golums">Golums</SelectItem>
                                                                        <SelectItem value="phoenixes">Phoenixes</SelectItem>
                                                                        <SelectItem value="mystiys">Mystiys</SelectItem>
                                                                        <SelectItem value="unicorns">Unicorns</SelectItem>
                                                                        <SelectItem value="other">Other</SelectItem>
                                                                    </SelectContent>
                                                                </Select>
                                                                <FieldError
                                                                    errors={fieldState.error ? [fieldState.error] : undefined}
                                                                />
                                                            </FieldContent>
                                                        </Field>
                                                    )}
                                                />

                                                {creatureInterest === 'other' && (
                                                    <FormField
                                                        control={form.control}
                                                        name="creatureOther"
                                                        render={({ field, fieldState }) => (
                                                            <Field data-invalid={!!fieldState.error}>
                                                                <FieldLabel htmlFor="creatureOther">Please specify</FieldLabel>
                                                                <FieldContent>
                                                                    <Input
                                                                        {...field}
                                                                        id="creatureOther"
                                                                        placeholder="Enter the creature you're looking forward to"
                                                                        disabled={form.formState.isSubmitting}
                                                                    />
                                                                    <FieldError
                                                                        errors={fieldState.error ? [fieldState.error] : undefined}
                                                                    />
                                                                </FieldContent>
                                                            </Field>
                                                        )}
                                                    />
                                                )}

                                                <FormField
                                                    control={form.control}
                                                    name="feedback"
                                                    render={({ field, fieldState }) => (
                                                        <Field data-invalid={!!fieldState.error}>
                                                            <FieldLabel htmlFor="feedback">
                                                                Anything else you’d love in the Realms?
                                                            </FieldLabel>
                                                            <FieldContent>
                                                                <Textarea
                                                                    {...field}
                                                                    id="feedback"
                                                                    placeholder="Share any additional thoughts or feedback"
                                                                    rows={4}
                                                                    disabled={form.formState.isSubmitting}
                                                                />
                                                                <FieldError
                                                                    errors={fieldState.error ? [fieldState.error] : undefined}
                                                                />
                                                            </FieldContent>
                                                        </Field>
                                                    )}
                                                />
                                            </div>

                                            <div className="flex items-center justify-end gap-4">
                                                <Button
                                                    type="reset"
                                                    variant="ghost"
                                                    onClick={() => {
                                                        form.reset()
                                                        setSubmitError(null)
                                                    }}
                                                    disabled={form.formState.isSubmitting}
                                                >
                                                    Reset
                                                </Button>
                                                <Button type="submit" disabled={form.formState.isSubmitting}>
                                                    {form.formState.isSubmitting && (
                                                        <Loader2 className="mr-2 size-4 animate-spin" />
                                                    )}
                                                    Get Early Access
                                                </Button>
                                            </div>
                                        </form>
                                    </Form>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </CardContent>
            </Card>
        </section>
    )
}

